import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, Save, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const IMAGES = [
  { key: "logo_main", label: "Logo principal" },
  { key: "logo_symbol", label: "Logomarca / símbolo" },
  { key: "app_icon", label: "Ícone do app/sistema" },
  { key: "logo_light", label: "Logo p/ fundo claro" },
  { key: "logo_dark", label: "Logo p/ fundo escuro" },
];
const COLORS = [
  { key: "color_primary", label: "Cor principal" },
  { key: "color_secondary", label: "Cor secundária" },
  { key: "color_accent", label: "Cor de destaque" },
];

export default function AdminBranding() {
  const { user } = useAuth();
  const { refresh } = useBranding();
  const isSuper = user?.permissions?.includes("system:admin");
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/branding").then((r) => setB(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);

  const onFile = (key, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 500000) return toast.error("Imagem muito grande (máx 500KB).");
    const reader = new FileReader(); reader.onload = () => setB((p) => ({ ...p, [key]: reader.result })); reader.readAsDataURL(file);
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/branding", b); setB(data); await refresh(); toast.success("Identidade visual atualizada."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!isSuper) return <p className="text-sm text-zinc-500">Apenas Super Admin pode configurar a identidade visual.</p>;
  if (!b) return <p className="text-zinc-500">Carregando...</p>;

  return (
    <div className="space-y-6" data-testid="admin-branding">
      <p className="text-sm text-zinc-400 flex items-center gap-2"><Palette className="h-4 w-4 text-[#FF4D00]" />Configure logos, ícone e cores da plataforma. As mudanças valem em todo o sistema.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {IMAGES.map(({ key, label }) => (
          <div key={key} className="bg-card border border-white/[0.06] rounded-xl p-4">
            <Label className="text-xs text-zinc-400">{label}</Label>
            <div className="mt-2 h-20 rounded-xl bg-[#09090B] flex items-center justify-center overflow-hidden">
              {b[key] ? <img src={b[key]} alt={label} className="h-full object-contain" /> : <span className="text-zinc-600 text-xs">sem imagem</span>}
            </div>
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-accent cursor-pointer"><Upload className="h-3.5 w-3.5" />Enviar imagem<input data-testid={`branding-file-${key}`} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(key, e)} /></label>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {COLORS.map(({ key, label }) => (
          <div key={key} className="bg-card border border-white/[0.06] rounded-xl p-4">
            <Label className="text-xs text-zinc-400">{label}</Label>
            <div className="flex items-center gap-2 mt-2">
              <input data-testid={`branding-color-${key}`} type="color" value={b[key] || "#000000"} onChange={(e) => setB({ ...b, [key]: e.target.value })} className="h-9 w-12 bg-transparent rounded-xl cursor-pointer" />
              <Input value={b[key] || ""} onChange={(e) => setB({ ...b, [key]: e.target.value })} className="bg-[#09090B] border-white/10 rounded-xl text-sm" />
            </div>
          </div>
        ))}
      </div>

      <Button data-testid="branding-save-btn" onClick={save} disabled={busy} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-2"><Save className="h-4 w-4" />Salvar identidade visual</Button>
    </div>
  );
}
